import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import {
  useFeedbackPrompt,
  type UserEngagement as FeedbackUserEngagement,
} from '@/hooks/useFeedbackPrompt';
import { FEEDBACK_SHOWN_SESSION_KEY } from '@/components/FeedbackPromptProvider';
import { isNativeApp } from '@/lib/platform';

// Extra columns from the review-prompt migration. Live on the shared
// user_engagement row alongside the feedback columns.
interface ReviewEngagementColumns {
  review_prompted_count: number;
  last_review_prompted_at: string | null;
  has_rated: boolean;
}

export type ReviewEngagement = FeedbackUserEngagement & ReviewEngagementColumns;

export const REVIEW_PROMPT_CONFIG = {
  minSessions: 2,
  lifetimeCap: 3,
  cooldownDays: 60,
} as const;

export const REVIEW_PROMPTED_SESSION_KEY = 'grounded_review_prompted_session';
export { FEEDBACK_SHOWN_SESSION_KEY };

/**
 * Pure eligibility predicate for the App Store review prompt. Does NOT check
 * the sessionStorage guard or the anti-stacking conditions — those depend on
 * runtime queue/session state and live in AppReviewPromptProvider.
 */
export function shouldShowReviewPrompt(engagement: ReviewEngagement | null | undefined): boolean {
  if (!engagement) return false;
  if (!isNativeApp()) return false;
  if (engagement.has_rated) return false;
  if (engagement.review_prompted_count >= REVIEW_PROMPT_CONFIG.lifetimeCap) return false;
  if (engagement.session_count < REVIEW_PROMPT_CONFIG.minSessions) return false;

  if (engagement.last_review_prompted_at) {
    const daysSince =
      (Date.now() - new Date(engagement.last_review_prompted_at).getTime()) /
      (1000 * 60 * 60 * 24);
    if (daysSince < REVIEW_PROMPT_CONFIG.cooldownDays) return false;
  }

  return true;
}

/**
 * Reads the same ['user-engagement', user.id] cache as useFeedbackPrompt
 * (populated by that hook — no duplicate fetch) and exposes mutations for the
 * three outcomes: accepted, declined (routed to feedback), and shown.
 *
 * Also calls useFeedbackPrompt() internally so its session-init logic runs
 * whether or not FeedbackPromptProvider is mounted above us. Its session-init
 * is guarded by sessionStorage, so double-calling is safe.
 */
export function useAppReviewPrompt() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Ensure feedback hook's cache/session init runs; we don't read its return
  // value directly — the provider consults shouldShowFeedbackPrompt on the
  // shared cache row instead, to avoid duplicated predicate logic.
  useFeedbackPrompt();

  const { data: engagement } = useQuery({
    queryKey: ['user-engagement', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('user_engagement')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) throw error;
      return (data as ReviewEngagement | null) ?? null;
    },
    enabled: !!user?.id,
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['user-engagement', user?.id] });
  }, [queryClient, user?.id]);

  const markShownMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !engagement) return;
      const { error } = await supabase
        .from('user_engagement')
        .update({
          last_review_prompted_at: new Date().toISOString(),
          review_prompted_count: engagement.review_prompted_count + 1,
        })
        .eq('user_id', user.id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const markRatedMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) return;
      const { error } = await supabase
        .from('user_engagement')
        .update({
          has_rated: true,
          last_review_prompted_at: new Date().toISOString(),
        })
        .eq('user_id', user.id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const markDeclinedMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) return;
      // "Not really" → route to feedback and permanently suppress review asks
      // by pinning the count at the lifetime cap.
      const { error } = await supabase
        .from('user_engagement')
        .update({
          last_review_prompted_at: new Date().toISOString(),
          review_prompted_count: REVIEW_PROMPT_CONFIG.lifetimeCap,
        })
        .eq('user_id', user.id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return {
    engagement: engagement ?? null,
    markShown: markShownMutation.mutateAsync,
    markRated: markRatedMutation.mutateAsync,
    markDeclined: markDeclinedMutation.mutateAsync,
  };
}
